import { memo, type ReactNode, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  ACHIEVEMENT_TIERS,
  type ACHIEVEMENT_TYPES,
} from '@didaskill/domain/user-achievements/constants';
import {
  Achievement,
  BUTTON_VARIANTS,
  Dialog,
  STICKER_SIZES,
  TYPOGRAPHY_VARIANTS,
  Typography,
} from '@didaskill/ui/design-system';

import type { QuestRequirement } from '../../users/quests/quest/Quest';
import { AvailableFrom } from './AvailableFrom';
import { QuestAction } from './QuestAction';
import messages from './QuestDialog.messages';
import * as styles from './QuestDialog.styles';
import { RemainingTime } from './RemainingTime';

/**
 * Props for the QuestDialog component.
 */
export type QuestDialogProps = {
  /**
   * Whether the popup is currently open.
   */
  isOpen: boolean;

  /**
   * Callback triggered when the popup is closed.
   */
  onClose: () => void;

  /**
   * End date of the quest.
   */
  endOfQuest?: Date;

  /**
   * Title of the quest displayed in the popup.
   */
  title: ReactNode;

  /**
   * Type of the achievement associated with the quest.
   */
  achievementType: ACHIEVEMENT_TYPES;

  /**
   * Number of credits the user can obtain upon completion of the quest.
   */
  creditsToBeObtained?: number;

  /**
   * List of requirements in the quest.
   * Each requirement represents a step of the quest and affects how the component is rendered:
   * - `completed: true` displays the requirement as completed and updates the associated icon.
   * - `completed: false` displays the requirement as not completed.
   * The number of completed requirements is used to determine whether the **Achievement** badge should be disabled (`isDisabled`) and to show the quest progress.
   */
  requirements: Array<QuestRequirement>;

  availableAfterDays: number;
};

const QuestDialogBase = ({
  isOpen,
  onClose,
  endOfQuest,
  title,
  achievementType,
  creditsToBeObtained = 0,
  availableAfterDays = 0,
  requirements,
}: QuestDialogProps) => {
  const { requirementsCompleted, requirementsToBeCompleted, isDisabled } = useMemo(() => {
    const total = requirements.length;
    const completed = requirements.reduce((acc, req) => acc + (req.isCompleted ? 1 : 0), 0);

    return {
      requirementsCompleted: completed,
      requirementsToBeCompleted: total,
      isDisabled: completed < total,
    };
  }, [requirements]);

  return (
    <Dialog isOpen={isOpen} onRequestClose={onClose} progress={0} size={Dialog.SIZES.AUTO}>
      <Dialog.Body css={styles.container}>
        <div css={styles.banner}>
          <div css={styles.achievementContainer}>
            <Achievement
              tier={ACHIEVEMENT_TIERS.QUEST}
              type={achievementType}
              size={STICKER_SIZES.GIANT}
              isDisabled={isDisabled}
            >
              {isDisabled && (
                <Typography variant={TYPOGRAPHY_VARIANTS.HEADING_1} css={styles.score}>
                  {requirementsCompleted}/{requirementsToBeCompleted}
                </Typography>
              )}
            </Achievement>
          </div>
        </div>

        {availableAfterDays > 0 && <AvailableFrom availableAfterDays={availableAfterDays} />}
        {endOfQuest && <RemainingTime endOfQuest={endOfQuest} />}

        <div>
          <Typography variant={TYPOGRAPHY_VARIANTS.HEADING_4} css={styles.title}>
            {title}
          </Typography>

          <Typography variant={TYPOGRAPHY_VARIANTS.BODY_MEDIUM_REGULAR} css={styles.label}>
            <FormattedMessage
              {...messages.LABEL}
              values={{
                count: requirementsToBeCompleted,
                credits: creditsToBeObtained,
              }}
            />
          </Typography>
          <div css={styles.actionsContainer}>
            {requirements.map((requirement) => (
              <QuestAction key={requirement.title} {...requirement} />
            ))}
          </div>
        </div>
      </Dialog.Body>

      <Dialog.Actions>
        <Dialog.Action onClick={onClose} variant={BUTTON_VARIANTS.PRIMARY}>
          <FormattedMessage {...messages.IS_UNDERSTOOD} />
        </Dialog.Action>
      </Dialog.Actions>
    </Dialog>
  );
};

QuestDialogBase.displayName = 'QuestDialog';

export const QuestDialog = memo(QuestDialogBase);
